import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import ClientLayout from "../components/ClientLayout";
import { Search, Clock, ImageOff } from "lucide-react";

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

function RecipeThumb({ r }) {
  const url = resolveImageUrl(r.image_url);
  if (!url) {
    return (
      <div className="w-full h-full grid place-items-center bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-muted))]">
        <div className="text-center">
          <ImageOff className="w-8 h-8 mx-auto opacity-50" />
          <div className="text-[10px] tracking-[0.2em] uppercase mt-1 opacity-70">No photo</div>
        </div>
      </div>
    );
  }
  return <img src={url} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />;
}

export default function ClientRecipes() {
  const { token, nameSlug = "client" } = useParams();
  const base = `/${nameSlug}/${token}`;
  const [recipes, setRecipes] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (tag) params.set("tag", tag);
    api.get(`/client/${token}/recipes?${params.toString()}`).then(({ data }) => {
      setRecipes(data.recipes);
      setTags(data.tags);
      setLoading(false);
    });
  }, [token, search, tag]);

  return (
    <ClientLayout>
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14" data-testid="client-recipes-page">
        <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">The kitchen</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-2">Recipe book.</h1>
        <p className="text-[hsl(var(--brand-muted))] mt-2 max-w-lg">Every meal in your plan, step-by-step. Cook with ease.</p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--brand-muted))]" />
            <input
              placeholder="Search recipes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-[hsl(var(--brand-border))] rounded-md bg-white"
              data-testid="client-recipes-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setTag("")} className={`px-3 py-1.5 text-xs rounded-full border ${tag === "" ? "bg-[hsl(var(--brand-sage))] text-white border-[hsl(var(--brand-sage))]" : "bg-white border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream-deep))]"}`} data-testid="client-tag-all">All</button>
            {tags.map((t) => (
              <button key={t} onClick={() => setTag(t)} className={`px-3 py-1.5 text-xs rounded-full border capitalize ${tag === t ? "bg-[hsl(var(--brand-sage))] text-white border-[hsl(var(--brand-sage))]" : "bg-white border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream-deep))]"}`} data-testid={`client-tag-${t}`}>{t}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-10 font-serif-italic text-[hsl(var(--brand-muted))]">Loading…</div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {recipes.map((r) => (
              <Link key={r.id} to={`${base}/recipes/${r.id}`} className="group bg-white border border-[hsl(var(--brand-border))] rounded-xl overflow-hidden card-lift" data-testid={`client-recipe-card-${r.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[hsl(var(--brand-cream-deep))]">
                  <RecipeThumb r={r} />
                </div>
                <div className="p-4">
                  <div className="font-display text-lg leading-tight">{r.name}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[hsl(var(--brand-muted))]">
                    {r.prep_time_minutes ? (<span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{r.prep_time_minutes}m</span>) : null}
                    <span>{r.ingredients?.length || 0} ingredients</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </ClientLayout>
  );
}
